import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

/**
 * Storage meter for VCR PROGRAM MODE — a slim strip under the header
 * showing the sum of every live upload-kind tape's `size` field, tape
 * count, and the single-tape cap.
 *
 * Now includes a DETAILS toggle that expands to show:
 *   • per-channel byte breakdown (largest channels first)
 *   • top-10 individual largest tapes (id / title / size / channel)
 *   • soft-deleted-residual count/bytes with a PURGE LEFTOVERS button
 *     that fires `POST /uploads/storage/purge-orphans` and refreshes.
 *
 * Refreshes on mount + `refreshKey` change (parent passes a value that
 * flips on channel / tape changes so the meter stays in sync after
 * upload / delete without a manual click).
 */
function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export default function StorageMeter({ apiBase, authHeaders, refreshKey, append }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownErr, setBreakdownErr] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  const fetchMeter = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/uploads/storage`, { headers: authHeaders() });
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }, [apiBase, authHeaders]);

  const fetchBreakdown = useCallback(async () => {
    setBreakdownLoading(true);
    setBreakdownErr(null);
    try {
      const res = await axios.get(`${apiBase}/uploads/storage/breakdown`, { headers: authHeaders() });
      setBreakdown(res.data);
    } catch (e) {
      setBreakdownErr(e.response?.data?.detail || e.message);
    } finally {
      setBreakdownLoading(false);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchMeter();
      if (!cancelled && showDetails) await fetchBreakdown();
    })();
    return () => { cancelled = true; };
  }, [refreshKey, showDetails, fetchMeter, fetchBreakdown]);

  const handlePurge = async () => {
    if (purging) return;
    setPurging(true);
    if (append) append("> PURGING SOFT-DELETED RESIDUALS...");
    try {
      const res = await axios.post(
        `${apiBase}/uploads/storage/purge-orphans`,
        {},
        { headers: authHeaders() }
      );
      const { processed, purged, failed, bytes_reclaimed } = res.data || {};
      if (append) {
        append(`> PURGE DONE — processed ${processed}, purged ${purged}, failed ${failed}, reclaimed ${fmtBytes(bytes_reclaimed)}`);
      }
      await fetchMeter();
      await fetchBreakdown();
    } catch (e) {
      if (append) append(`> PURGE FAILED: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPurging(false);
    }
  };

  if (err) {
    return (
      <div className="text-[10px] opacity-60 mb-2 tracking-[0.25em]" data-testid="storage-meter-error">
        &gt; STORAGE METER OFFLINE ({err})
      </div>
    );
  }
  if (!data) {
    return (
      <div className="text-[10px] opacity-50 mb-2 tracking-[0.25em]" data-testid="storage-meter-loading">
        &gt; MEASURING SHELF...
      </div>
    );
  }
  return (
    <div className="mb-3">
      <div
        className="flex items-center gap-3 border border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] px-3 py-1.5 text-[10px] md:text-[11px] tracking-[0.22em]"
        data-testid="storage-meter"
      >
        <span className="opacity-70 shrink-0">┌─ STORAGE ON SHELF ─┐</span>
        <span className="text-[var(--phosphor)] font-bold tabular-nums" data-testid="storage-meter-total">
          {fmtBytes(data.total_bytes)}
        </span>
        <span className="opacity-50">·</span>
        <span className="tabular-nums" data-testid="storage-meter-count">
          {data.count} TAPE{data.count === 1 ? "" : "S"}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setShowDetails((s) => !s)}
          data-testid="storage-meter-details-toggle"
          className="text-[10px] opacity-70 hover:opacity-100 border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] px-2 py-0.5 tracking-widest hover:bg-[color-mix(in_srgb,var(--phosphor)_15%,transparent)]"
        >
          {showDetails ? "HIDE DETAILS" : "DETAILS"}
        </button>
        <span className="opacity-50 shrink-0">
          CAP · {fmtBytes(data.per_file_cap_bytes)}
        </span>
      </div>

      {showDetails && (
        <div
          data-testid="storage-meter-details"
          className="border border-t-0 border-[color-mix(in_srgb,var(--phosphor)_25%,transparent)] px-3 py-2 text-[10px] leading-relaxed"
        >
          {breakdownLoading && <div className="opacity-60">&gt; SCANNING SHELF...</div>}
          {breakdownErr && <div className="opacity-70 text-red-400">&gt; ERROR: {breakdownErr}</div>}
          {breakdown && !breakdownLoading && (
            <>
              {/* Soft-deleted residual — actionable */}
              <div
                className={`flex items-center gap-2 border px-2 py-1.5 mb-2 ${
                  breakdown.soft_deleted_residual.count > 0
                    ? "border-[#FFCC00]/70 text-[#FFCC00] bg-[#FFCC00]/5"
                    : "border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] opacity-70"
                }`}
                data-testid="storage-residuals"
              >
                <span className="tracking-widest">
                  ⚠ LEFTOVER BLOBS
                </span>
                <span className="tabular-nums">
                  {breakdown.soft_deleted_residual.count} TAPE
                  {breakdown.soft_deleted_residual.count === 1 ? "" : "S"} ·
                  {" "}{fmtBytes(breakdown.soft_deleted_residual.bytes)}
                </span>
                <div className="flex-1" />
                {breakdown.soft_deleted_residual.count > 0 && (
                  <button
                    onClick={handlePurge}
                    disabled={purging}
                    data-testid="storage-purge-button"
                    className="text-[10px] tracking-widest border border-[#FFCC00] px-2 py-0.5 hover:bg-[#FFCC00] hover:text-black transition-colors disabled:opacity-40"
                  >
                    {purging ? "PURGING..." : "PURGE LEFTOVERS"}
                  </button>
                )}
              </div>
              <div className="text-[9px] opacity-60 leading-snug mb-2">
                Leftovers are tapes you deleted where the storage service refused
                the purge (usually during a storage outage). Running PURGE
                LEFTOVERS re-attempts each blob and only clears the DB path on
                success — safe to run repeatedly.
              </div>

              {/* Per-channel breakdown */}
              <div className="mb-2">
                <div className="opacity-70 tracking-widest mb-1">BY CHANNEL</div>
                {breakdown.by_channel.length === 0 && (
                  <div className="opacity-50 pl-2">(no live uploads yet)</div>
                )}
                {breakdown.by_channel.map((r) => (
                  <div
                    key={r.channel_id}
                    className="flex items-center gap-2 pl-2"
                    data-testid={`storage-channel-${r.channel_id}`}
                  >
                    <span className="opacity-60 tabular-nums w-6">
                      {r.channel_number ?? "?"}
                    </span>
                    <span className="truncate flex-1">{r.channel_name}</span>
                    <span className="tabular-nums opacity-80">{r.count}×</span>
                    <span className="tabular-nums text-[var(--phosphor)] w-20 text-right">
                      {fmtBytes(r.bytes)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Top-10 largest */}
              {breakdown.largest.length > 0 && (
                <div>
                  <div className="opacity-70 tracking-widest mb-1">
                    LARGEST TAPES (TOP 10)
                  </div>
                  {breakdown.largest.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center gap-2 pl-2"
                      data-testid={`storage-largest-${r.id}`}
                    >
                      <span className="opacity-60 tabular-nums w-6">
                        {r.channel_number ?? "?"}
                      </span>
                      <span className="truncate flex-1">{r.title}</span>
                      <span className="tabular-nums text-[var(--phosphor)] w-20 text-right">
                        {fmtBytes(r.size)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
